import { useMemo } from "react";
import { TriangleAlert } from "lucide-react";
import { Color } from "utils/theme";
import { useStore } from "hooks/use-store";
import { SeaSicknessSeverityInfo } from "types/whole-body-vibration";

const LOW_THRESHOLD = 10;
const MODERATE_THRESHOLD = 25;
const HIGH_THRESHOLD = 50;

export const SeaSicknessAlert = () => {
  const wholeBodyVibration = useStore((state) => state.wholeBodyVibration);

  const maxSeaSicknessIndex = useMemo(() => {
    return Math.max(
      0,
      ...Object.values(wholeBodyVibration).map(
        (sensor) => sensor.motion_sickness_index?.value ?? 0
      )
    );
  }, [wholeBodyVibration]);

  // If no data or below low risk threshold, do not show alert
  if (
    Object.keys(wholeBodyVibration).length === 0 ||
    maxSeaSicknessIndex < LOW_THRESHOLD
  ) {
    return null;
  }

  const severityLevels: Record<string, SeaSicknessSeverityInfo> = {
    low: {
      label: "Low sea sickness risk",
      description:
        "Crew members might experience some sea sickness. Please take necessary precautions.",
      smallIcon: (
        <TriangleAlert
          className="w-6 h-6"
          style={{ color: Color.Amber[400] }}
        />
      ),
      largeIcon: (
        <TriangleAlert
          className="w-16 h-16"
          style={{ color: Color.Amber[400] }}
        />
      ),
    },
    moderate: {
      label: "Moderate sea sickness risk",
      description:
        "Crew members might experience moderate sea sickness. Please take necessary precautions.",
      smallIcon: (
        <TriangleAlert
          className="w-6 h-6"
          style={{ color: Color.Orange[400] }}
        />
      ),
      largeIcon: (
        <TriangleAlert
          className="w-16 h-16"
          style={{ color: Color.Orange[400] }}
        />
      ),
    },
    high: {
      label: "High sea sickness risk",
      description:
        "Crew members are likely to experience severe sea sickness. Immediate action is recommended.",
      smallIcon: (
        <TriangleAlert className="w-6 h-6" style={{ color: Color.Rose[600] }} />
      ),
      largeIcon: (
        <TriangleAlert
          className="w-16 h-16"
          style={{ color: Color.Rose[600] }}
        />
      ),
    },
  };

  let severity: SeaSicknessSeverityInfo;
  if (maxSeaSicknessIndex >= HIGH_THRESHOLD) {
    severity = severityLevels.high;
  } else if (maxSeaSicknessIndex >= MODERATE_THRESHOLD) {
    severity = severityLevels.moderate;
  } else {
    severity = severityLevels.low;
  }

  return (
    <div
      className="flex items-center overflow-hidden"
      style={{
        backgroundColor: Color.Primary[900],
      }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center mr-0.5 shrink-0">
        {severity.smallIcon}
      </div>
      <p className="whitespace-nowrap text-ellipsis overflow-hidden">
        {severity.label}
      </p>
    </div>
  );
};
